import { useCallback, useEffect, useState } from 'react';
import type {
  BriefingToken,
  BriefingSubscription,
  BriefingSubscriberRole,
} from '@tsr/shared-types';
import {
  createBriefingToken,
  listBriefingTokens,
  revokeBriefingToken,
  listBriefingSubscriptions,
  createBriefingSubscription,
  deleteBriefingSubscription,
} from './api';
import { getSessionId } from './lib/operatorActions';

/**
 * B.1 piece F — the Briefing-mode management sidebar (operator view only). The
 * layer panel is hidden in Briefing mode; this replaces it with the two things an
 * operator manages for the elected-official audience: the tokenized read-only
 * links (Active Tokens) and the per-incident email subscribers who get the
 * operational-period auto-push.
 */

const ROLES: { value: BriefingSubscriberRole; label: string }[] = [
  { value: 'minister', label: 'Minister' },
  { value: 'mayor', label: 'Mayor' },
  { value: 'agency_head', label: 'Agency head' },
  { value: 'other', label: 'Other' },
];

export interface BriefingAdminPanelProps {
  /** Incident in scope (enables subscriber management); null → tokens only. */
  incidentId: string | null;
  currentBbox: { west: number; south: number; east: number; north: number };
}

export function BriefingAdminPanel({ incidentId }: BriefingAdminPanelProps): JSX.Element {
  const [tokens, setTokens] = useState<BriefingToken[]>([]);
  const [subs, setSubs] = useState<BriefingSubscription[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<BriefingSubscriberRole>('minister');
  const [busy, setBusy] = useState(false);

  const refreshTokens = useCallback(async (signal?: AbortSignal) => {
    try {
      setTokens(await listBriefingTokens(signal));
    } catch {
      /* non-fatal */
    }
  }, []);

  const refreshSubs = useCallback(
    async (signal?: AbortSignal) => {
      if (!incidentId) {
        setSubs([]);
        return;
      }
      try {
        setSubs(await listBriefingSubscriptions(incidentId, signal));
      } catch {
        /* non-fatal */
      }
    },
    [incidentId],
  );

  useEffect(() => {
    const ctl = new AbortController();
    void refreshTokens(ctl.signal);
    void refreshSubs(ctl.signal);
    return () => ctl.abort();
  }, [refreshTokens, refreshSubs]);

  async function onCreateToken(): Promise<void> {
    setBusy(true);
    try {
      await createBriefingToken({
        session_id: getSessionId(),
        incident_id: incidentId ?? null,
      });
      await refreshTokens();
    } finally {
      setBusy(false);
    }
  }

  async function onRevoke(token: string): Promise<void> {
    await revokeBriefingToken(token, { session_id: getSessionId() });
    await refreshTokens();
  }

  async function onAddSub(): Promise<void> {
    if (!incidentId || !email.trim()) return;
    setBusy(true);
    try {
      await createBriefingSubscription({
        session_id: getSessionId(),
        incident_id: incidentId,
        email_address: email.trim(),
        role,
      });
      setEmail('');
      await refreshSubs();
    } finally {
      setBusy(false);
    }
  }

  async function onRemoveSub(id: string): Promise<void> {
    await deleteBriefingSubscription(id);
    await refreshSubs();
  }

  return (
    <section
      className="mx-10 mb-8 grid gap-6 rounded-xl border border-slate-700 bg-slate-900/60 p-6 lg:grid-cols-2"
      data-testid="briefing-admin"
    >
      {/* Active Tokens */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-100">Active read-only links</h3>
          <button
            type="button"
            disabled={busy}
            onClick={() => void onCreateToken()}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
            data-testid="create-token"
          >
            Create link
          </button>
        </div>
        {tokens.length === 0 ? (
          <p className="text-sm text-slate-400">No links yet.</p>
        ) : (
          <ul className="space-y-2">
            {tokens.map((t) => (
              <li
                key={t.id}
                className="rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className={t.active ? 'text-emerald-400' : 'text-slate-500'}>
                    {t.active ? 'Active' : t.revoked_at ? 'Revoked' : 'Expired'}
                  </span>
                  {t.active && (
                    <button
                      type="button"
                      onClick={() => void onRevoke(t.token)}
                      className="text-rose-400 hover:text-rose-300"
                    >
                      Revoke
                    </button>
                  )}
                </div>
                <input
                  readOnly
                  value={t.url}
                  onFocus={(e) => e.currentTarget.select()}
                  className="mt-1 w-full truncate rounded bg-slate-800 px-2 py-1 text-xs text-slate-300"
                />
                <p className="mt-1 text-xs text-slate-500">
                  Expires {new Date(t.expires_at).toLocaleString('en-CA')} · {t.accessed_count} view(s)
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Briefing Subscribers */}
      <div>
        <h3 className="mb-3 text-lg font-semibold text-slate-100">Briefing email subscribers</h3>
        {!incidentId ? (
          <p className="text-sm text-slate-400">
            Subscriber management is available when an incident is in scope (open an incident-anchored
            briefing).
          </p>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap gap-2">
              <input
                type="email"
                placeholder="email@example.ca"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="min-w-0 flex-1 rounded bg-slate-800 px-2 py-1.5 text-sm text-slate-100"
                data-testid="sub-email"
              />
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as BriefingSubscriberRole)}
                className="rounded bg-slate-800 px-2 py-1.5 text-sm text-slate-100"
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={busy || !email.trim()}
                onClick={() => void onAddSub()}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
                data-testid="add-sub"
              >
                Add
              </button>
            </div>
            {subs.length === 0 ? (
              <p className="text-sm text-slate-400">No subscribers yet.</p>
            ) : (
              <ul className="space-y-2">
                {subs.map((s) => (
                  <li
                    key={s.id}
                    className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-sm"
                  >
                    <span>
                      <span className="text-slate-100">{s.email_address}</span>{' '}
                      <span className="text-slate-500">· {s.role}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => void onRemoveSub(s.id)}
                      className="text-rose-400 hover:text-rose-300"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </section>
  );
}
